import { client } from '@/client';
import {
  CustomRole,
  CustomRoleResult,
  ListCustomRolesResponse,
  SaveCustomRoleRequest,
} from '@/client/custom-roles';
import { GenericForbiddenApiError } from '@/hooks/query';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

export const customRolesQueryKey = 'customRoles';

export const useCustomRoles = (
  accountId: string,
  options?: UseQueryOptions,
): UseQueryResult<ListCustomRolesResponse> & {
  customRoles: CustomRoleResult[];
} => {
  const { data, ...rest } = useQuery(
    [customRolesQueryKey, accountId],
    () => client.customRoles.getCustomRoles(accountId),
    {
      enabled: !!accountId,
      ...(options as object),
    },
  );

  return {
    data,
    customRoles: data?.result ?? [],
    ...rest,
  } as UseQueryResult<ListCustomRolesResponse> & {
    customRoles: CustomRoleResult[];
  };
};

export const useCustomRole = (
  accountId: string,
  id?: string,
  options?: UseQueryOptions,
): UseQueryResult<CustomRoleResult> & {
  customRole: CustomRoleResult | undefined;
} => {
  const { data, ...rest } = useQuery(
    [customRolesQueryKey, accountId, id],
    () => client.customRoles.getCustomRole(accountId, id as string),
    {
      enabled: !!accountId && !!id,
      ...(options as object),
    },
  );

  return {
    data,
    customRole: data,
    ...rest,
  } as UseQueryResult<CustomRoleResult> & {
    customRole: CustomRoleResult | undefined;
  };
};

export const useSaveCustomRole = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    CustomRole,
    GenericForbiddenApiError,
    { accountId: string; id?: string; payload: SaveCustomRoleRequest }
  >(
    'saveCustomRole',
    ({ accountId, id, payload }) =>
      id
        ? client.customRoles.updateCustomRole(accountId, id, payload)
        : client.customRoles.createCustomRole(accountId, payload),
    {
      onSuccess: (_result, { accountId }) =>
        queryClient.invalidateQueries([customRolesQueryKey, accountId]),
    },
  );

  return { save: mutateAsync, ...(rest as any) };
};

export const useDeleteCustomRole = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    { accountId: string; id: string }
  >(
    'deleteCustomRole',
    ({ accountId, id }) => client.customRoles.deleteCustomRole(accountId, id),
    {
      onSuccess: (_result, { accountId }) =>
        queryClient.invalidateQueries([customRolesQueryKey, accountId]),
    },
  );

  return { remove: mutateAsync, ...(rest as any) };
};
